/**
 * @providesModule CreateProfileScreenV2
 *
 */

import AbstractEditProfileScreen, {
  AbstractEditProfileScreenProps,
  AbstractEditProfileScreenState,
} from 'AbstractEditProfileScreen';
import APIUtilities from 'APIUtilities';
import AuthenticationController from 'AuthenticationController';
import {ASYNC_FTUE_START, HYBRID_FTUE_START, SYNC_FTUE_START} from 'AWSConstants';
import AWSManager from 'AWSManager';
import ClientPropertyConstants from 'ClientPropertyConstants';
import CurrentUserStore from 'CurrentUserStore';
import styles from 'EditProfileScreenStyle';
import FTUEController from 'FTUEController';
import FTUETournamentController, {FTUETournamentsResponse} from 'FTUETournamentController';
import GlobalEventEmitter from 'GlobalEventEmitter';
import HSConstants from 'HomeScreenConstants';
import I18n, {_m} from 'INTL';
import Label from 'Label';
import LiveEventFTUEFlowController from 'LiveEventFTUEFlowController';
import LoadingIndicator from 'LoadingIndicator';
import NavigationManager from 'NavigationManager';
import Optional from 'Optional';
import OrientationTransitionController from 'OrientationTransitionController';
import React from 'react';
import {Trans} from 'react-i18next';
import * as Animatable from 'react-native-animatable';
import {Navigator} from 'react-native-deprecated-custom-components';
import {WebView} from 'react-native-webview';
import SkillzImage from 'SkillzImage';
import SkillzStyle from 'SkillzStyle';
import SkillzSVG from 'SkillzSVG';
import SyncFTUEController, {FTUE_TYPE} from 'SyncFTUEController';
import {getRandomAvatar} from 'RandomizeAvatarUtil';

import {
  Animated,
  BackHandler,
  Dimensions,
  GestureResponderEvent,
  NativeModules,
  Platform,
  SafeAreaView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
  ViewStyle,
} from 'react-native';

import {SkillzButton} from '@skillz-rn/atoms';
import CurrencyController from 'CurrencyController';
import FTUEConversionsController from 'FTUEConversionsController';
import {debounce} from 'lodash';
import Tournament from 'SKZTournament';
import {SKZUserInterface} from 'SKZUser';
import {TERM_OF_SERVICE_URL} from 'EmailSignInView';
import WebDeepLinkHandler from 'WebDeepLinkHandler';
import SkillzRouter from 'SkillzRouter';
import { TelemetryEventName } from 'TelemetryConstants';
import { trackTelemetryEvent } from 'TrackTelemetryEvent';

const {SkillzModule} = NativeModules;

const {textColorPrimary} = SkillzStyle.fullStyles;

const CHANGE_MESSAGE = I18n.message('You can modify it anytime.');
const NEXT_BUTTON_TITLE = I18n.message('Next');
const ERROR_TITLE = I18n.message('Error');
const ERROR_MESSAGE = I18n.message(`Uh oh! Doesn't look like that worked. Please try again.`);
const INVALID_USERNAME_MESSAGE = I18n.message('This username already exists or is invalid.');
const TERMS_OF_SERVICE_URL = 'https://skillz.com/legal';
type CreateProfileScreenV2Props = AbstractEditProfileScreenProps & {
  shouldAllowExit: boolean;
  shouldAllowPop: boolean;
  navigator: Navigator;
};
type CreateProfileScreenV2State = AbstractEditProfileScreenState & {
  isButtonDisabled: boolean;
  showTermsOfUse: boolean;
  opacityAccountCreation: Animated.Value;
  joinedMatch?: boolean;
};

// This is acting as a sort of dispatch once token, it will prevent this file from calling SkillzModule.reactSDKHasLoaded
// more than once per run of the js
let hasNotifiedReady = false;

interface FooterButtonOptions {
  buttonLabel: string;
  onPress: (event: GestureResponderEvent) => void;
  accessibilityLabel: string;
  testID: string;
}

const TEST_IDS = {
  CREATE_PROFILE_SCREEN_FOOTER_BACK_BUTTON: 'back_button',
  CREATE_PROFILE_SCREEN_FOOTER_FIND_EVENT_BUTTON: 'find_event',
  CREATE_PROFILE_SCREEN_FOOTER_SIGN_IN_BUTTON: 'sign_in',
  CREATE_PROFILE_SCREEN_HELP_MESSAGE: 'help_message',
  CREATE_PROFILE_SCREEN_CONTAINER: 'create_profile_container',
  CREATE_PROFILE_SCREEN_NEXT_BUTTON: 'next_button',
  TAP: 'tap_here',
  EDIT_PROFILE_SCREEN_SUBMIT_BUTTON: 'submit_button',
  EDIT_PROFILE_SCREEN_CHANGE_MESSAGE: 'change_message',
  SIGN_IN: 'sign_in',
  EDIT_PROFILE_SCREEN_PICK_INFO_TITLE: 'pick_info_title',
  EDIT_PROFILE_SCREEN_USERNAME_INPUT: 'username_input',
  EDIT_PROFILE_SCREEN_PICK_AVATAR: 'pick_avatar',
};

export default class CreateProfileScreenV2 extends AbstractEditProfileScreen<
  CreateProfileScreenV2Props,
  CreateProfileScreenV2State
> {
  firstSyncTournament?: FTUETournamentsResponse['sync'];
  firstFTUETournament?: Tournament;
  animatableAvatar: any;
  animatedUsernameView: any;
  matchTransactionId: string | undefined;

  constructor(props: CreateProfileScreenV2Props) {
    super(props);

    this.state = {
      ...this._initialState(props),
      isButtonDisabled: false,
      showTermsOfUse: false,
      opacityAccountCreation: new Animated.Value(1),
    };
    this._setTOS = this._setTOS.bind(this);
  }

  componentDidMount() {
    super.componentDidMount();

    if (Platform.OS == 'android') {
      SkillzModule.setEnabledKeyboardManager(true);

      BackHandler.addEventListener('hardwareBackPress', this._handleAndroidBack);
    }
    CurrencyController.fetchUserCurrency(this.props.initialUser, true); // Just used to build the cache for future game play

    AWSManager.trackEvent('FTUE_CREATE_PROFILE_V2_LOADED');
    trackTelemetryEvent({
      event: TelemetryEventName.SIGN_UP_PAGE_LOAD,
      includeUserWalletDetails: true,
      includeUserDetails: true,
    });
  }

  componentWillUnmount() {
    if (Platform.OS == 'android') {
      SkillzModule.setEnabledKeyboardManager(false);

      BackHandler.removeEventListener('hardwareBackPress', this._handleAndroidBack);
    }
  }

  _notifySDKHasLoaded = () => {
    if (!hasNotifiedReady) {
      SkillzModule.reactSDKHasLoaded?.();
      hasNotifiedReady = true;
    }
  };

  _handleAndroidBack = (): boolean => {
    if (this.props.shouldAllowExit) {
      SkillzModule.exitSkillz();
    } else if (this.props.shouldAllowPop) {
      NavigationManager.pop();
    }

    // consume the event either way - back button should do nothing if we aren't allowed to exit skillz
    return true;
  };

  _submitProfileChanges(completion: (success: boolean) => void) {
    let updates: Promise<Object>[] = [];
    let isUpdatingUsername = false;
    updates = this._getAvatarUpdate(updates);
    const isUpdatingAvatar = updates.length === 0 ? false : true;
    if (this.state.username !== this.props.initialUser.username) {
      const usernameValidation = this._validateUsername();
      if (!usernameValidation) {
        completion(false);
        return;
      } else {
        updates = this._getUsernameUpdate(updates);
        isUpdatingUsername = true;
      }
    }
    if (updates && updates.length === 0) {
      console.debug('Edit profile: will not update username or avatar in ts');
      completion(true);
      return;
    } else {
      this._doProfileUpdatesV2(updates, isUpdatingUsername, isUpdatingAvatar, completion);
    }
  }

  _doProfileUpdatesV2(
    updates: Array<any>,
    isUpdatingUsername: boolean,
    isUpdatingAvatar: boolean,
    completion: (success: boolean) => void
  ) {
    LoadingIndicator.show();
    Promise.all(updates)
      .then((results: Array<any>) => {
        if (isUpdatingAvatar && isUpdatingUsername) {
          const newAvatarUrl = Platform.OS === 'ios' ? results[0].avatar_url : results[0].avatarUrl;
          const newUserInfo = this._setAvatarUrl(results[1], newAvatarUrl, completion);
          this._doUpdates(newUserInfo, completion);
        } else if (isUpdatingAvatar && !isUpdatingUsername) {
          CurrentUserStore.getUserData(false)
            .then((userInfo: Object) => {
              const newAvatarUrl =
                Platform.OS === 'ios' ? results[0].avatar_url : results[0].avatarUrl;
              const newUserInfo = this._setAvatarUrl(userInfo, newAvatarUrl, completion);
              this._doUpdates(newUserInfo, completion);
            })
            .catch((error: string) => {
              this._handleError(
                error,
                'Error getting user information in edit profile:',
                ERROR_MESSAGE,
                completion,
                false
              );
            });
        } else if (!isUpdatingAvatar && isUpdatingUsername) {
          this._doUpdates(results[0], completion);
        }
      })
      .catch((error: string) => {
        console.log('Error updating user information in edit profile:', error);
        if (error?.includes?.('username')) {
          this._handleError(
            error,
            'Error updating user information in edit profile:',
            INVALID_USERNAME_MESSAGE,
            completion,
            true
          );
        } else if (error?.includes?.('picture')) {
          this._handleError(
            error,
            'Error updating user information in edit profile:',
            error,
            completion,
            false
          );
        } else {
          this._handleError(
            error,
            'Error updating user information in edit profile:',
            ERROR_MESSAGE,
            completion,
            false
          );
        }
      });
  }

  _setAvatarUrl(
    result: Object,
    newAvatarUrl: string,
    completion: (success: boolean) => void
  ): Object {
    const newUserInfo = this._copyUserInfo(result, completion);
    newUserInfo.avatar_url = newAvatarUrl;
    return newUserInfo;
  }

  _doUpdates(userInfo: Object, completion: (success: boolean) => void) {
    CurrentUserStore.updateUserData(userInfo);
    LoadingIndicator.hide();
    completion(true);
  }

  _copyUserInfo(result: Object, completion: (success: boolean) => void): SKZUserInterface {
    try {
      const newUserInfo = JSON.parse(JSON.stringify(result));
      return newUserInfo;
    } catch (error) {
      this._handleError(
        error as string,
        'Error parsing user information in edit profile:',
        ERROR_MESSAGE,
        completion,
        false
      );
      return {} as SKZUserInterface;
    }
  }

  _handleError(
    error: string,
    warnMessage: string,
    errorMessage: string,
    completion: (success: boolean) => void,
    handleCompletion: boolean
  ) {
    LoadingIndicator.hide();
    console.warn(warnMessage, error);
    this._displayError(errorMessage, ERROR_TITLE);
    if (handleCompletion) {
      completion(false);
    }
  }

  // HANDLERS

  // Debounce the done handler so we don't get multiple profile update requests/
  // FTUE match joins
  _onPressDone = debounce(
    () => {
      trackTelemetryEvent({
        event: TelemetryEventName.SIGN_UP_CTA_CLICK,
        payload: {
          cta: 'next',
        },
      });
      this._submitProfileChanges(async (success: boolean) => {
        if (success) {
          GlobalEventEmitter.emit('RefreshUserProfile');
          AWSManager.trackEvent('CREATE_PROFILE_NEXT_PRESSED', {
            type: ClientPropertyConstants.getFTUESkipMatchTutorial() ? 'SKIP_FTUE' : 'START_FTUE',
          });
          trackTelemetryEvent({
            event: TelemetryEventName.SIGN_UP_SUCCESS,
            payload: {
              is_guest_account: !CurrentUserStore?.isSavedAccount(),
              sign_up_status: true,
            },
          });

          if (ClientPropertyConstants.getFTUESkipMatchTutorial()) {
            this.saveAccountForSSO();
            this._skipFTUEMatchesAndRenderHomeScreen();
            return;
          }

          if (!this._canShowSyncFTUE() && !ClientPropertyConstants?.getSync1vNEnabled()) {
            OrientationTransitionController.updateOrientationToLandscape();
          }
          this.saveAccountForSSO();
          //here if we have a valid live event match code, just skip the Z games
          if (await WebDeepLinkHandler.checkDeepLink()) {
            this._markFTUEStepComplete();
            FTUEController.skipFTUE();
            SyncFTUEController.setSyncFTUEComplete(true);
            GlobalEventEmitter.emit('SyncFTUESkipped');
            const didHandleDeepLink = await WebDeepLinkHandler.checkAndHandleDeepLink();
            if (!didHandleDeepLink) {
              SkillzRouter.backToRoot();
            } else {
              return;
            }
          }
          this.transitionToFTUEMatch();
        } else {
          trackTelemetryEvent({
            event: TelemetryEventName.SIGN_UP_SUCCESS,
            payload: {
              is_guest_account: !CurrentUserStore?.isSavedAccount(),
              sign_up_status: false,
            },
          });
        }
      });
    },
    750,
    {leading: true, trailing: false}
  );

  _handleBack = () => {
    SkillzModule.exitSkillz();
  };

  _handleFindEvent = () => {
    if (ClientPropertyConstants.getFTUEMatchCodeFocused()) {
      AWSManager.trackEvent('FTUE_MATCH_CODE_TAP_HERE');
    }
    trackTelemetryEvent({
      event: TelemetryEventName.SIGN_UP_CTA_CLICK,
      payload: {
        cta: 'live event code',
      },
    });
    GlobalEventEmitter.emit('SetHomescreenInitialTab', HSConstants.STREAM_INDEX);
    this.setState({
      isButtonDisabled: true,
    });
    LiveEventFTUEFlowController.next(
      this,
      this.props.initialUser,
      {
        username: this.state.username,
        avatarUrl: this.state.avatar_url,
        avatarPath: this.avatarPath,
      },
      this.firstFTUETournament,
      this.props.navigator
    );
    setTimeout(() => {
      this.setState({
        isButtonDisabled: false,
      });
    }, 100); // eslint-disable-line no-magic-numbers
  };

  _handleSignIn = () => {
    AWSManager.trackEvent('CREATE_PROFILE_SIGN_IN_PRESSED');
    trackTelemetryEvent({
      event: TelemetryEventName.SIGN_UP_CTA_CLICK,
      payload: {
        cta: 'sign in',
      },
    });
    CurrentUserStore.clearUserCache();
    AuthenticationController.displaySignInScreen(true, undefined, undefined, true);
  };

  saveAccountForSSO() {
    SkillzModule.saveAccountForSSO?.(this.state.username, this.props.initialUser?.id);
  }

  // MARK: First Tournament Handling
  async _handleTournamentFetch(
    ftueTournaments: FTUETournamentsResponse,
    shouldTransitionToVersus = false
  ) {
    if (FTUEConversionsController.enabled && !FTUEConversionsController.completed) {
      await FTUEConversionsController.load();
    }

    if (
      !SyncFTUEController.canShowSyncFTUE() ||
      ClientPropertyConstants.getFTUEType() === FTUE_TYPE.HYBRID
    ) {
      return super._handleTournamentFetch(ftueTournaments as any, shouldTransitionToVersus);
    }

    const firstSyncTournament = ftueTournaments.sync;
    if (!firstSyncTournament) {
      return super._handleTournamentFetch(ftueTournaments as any, shouldTransitionToVersus);
    }

    this.firstSyncTournament = firstSyncTournament;
    if (this.shouldTransitionOnFetchCompletion) {
      return this.transitionToFTUEMatch();
    }
  }

  _canShowSyncFTUE(): boolean {
    const canShowSyncFTUE =
      ClientPropertyConstants.getFTUEType() === FTUE_TYPE.SYNC && !!this.firstSyncTournament;
    return SyncFTUEController.canShowSyncFTUE() && canShowSyncFTUE;
  }

  _canShowHybridFTUE(): boolean {
    const canShowHybridFTUE =
      ClientPropertyConstants.getFTUEType() === FTUE_TYPE.HYBRID && !!this.firstFTUETournament;
    return SyncFTUEController.canShowSyncFTUE() && canShowHybridFTUE;
  }

  _canShowAsyncFTUE(): boolean {
    const shouldShowAsyncFTUE = ClientPropertyConstants.getFTUEType() === FTUE_TYPE.ASYNC;
    const unableToShowSyncOrHybridFTUE = !this._canShowSyncFTUE() && !this._canShowHybridFTUE();
    return !!this.firstFTUETournament && (shouldShowAsyncFTUE || unableToShowSyncOrHybridFTUE);
  }

  // Called by subclasses in order to start a FTUE match
  async transitionToFTUEMatch() {
    GlobalEventEmitter.emit('StartFirstFTUEMatch');
    if (SyncFTUEController.shouldSkipFTUESyncBotMissing()) {
      // If we have a sync game with no trainer bot, we should skip the FTUE entirely
      this._markFTUEStepComplete();
      FTUEController.skipFTUE();
      SyncFTUEController.setSyncFTUEComplete(true);
      GlobalEventEmitter.emit('SyncFTUESkipped');
      NavigationManager.popToRoot();
    } else if (!FTUEController.shouldGoToVersusFromProfile()) {
      this._markFTUEStepComplete();
      GlobalEventEmitter.emit('suspendHomeScreenState', {shouldSuspend: false});
      NavigationManager.pop();
    } else if (this._canShowHybridFTUE()) {
      AWSManager.trackEvent(HYBRID_FTUE_START);
      return this._pushVersusFTUE();
    } else if (this._canShowSyncFTUE()) {
      AWSManager.trackEvent(SYNC_FTUE_START);
      LoadingIndicator.show();
      if (this.firstSyncTournament) {
        SyncFTUEController.startFirstSyncBotMatch({
          templateSet: this.firstSyncTournament.gameMode,
          tournament: this.firstSyncTournament.tournament,
        });
      }
      this._markFTUEStepComplete();
    } else if (this._canShowAsyncFTUE()) {
      AWSManager.trackEvent(ASYNC_FTUE_START);
      return this._pushVersusFTUE();
      // }
    } else {
      if (Platform.OS !== 'web') {
        LoadingIndicator.show();
      }
      return this._attemptFirstTournamentFetch(true);
    }
  }

  _onRefreshAvatarPressV2 = () => {
    if (this.props.isFTUE) {
      AWSManager.trackEvent('FTUE_REFRESH_AVATAR');
    } else {
      AWSManager.trackEvent('PROFILE_REFRESH_AVATAR');
    }
    trackTelemetryEvent({
      event: TelemetryEventName.SIGN_UP_CTA_CLICK,
      payload: {
        cta: 'player profile shuffle',
      },
    });

    this.animatableAvatar.lightSpeedOut(300).then(() => {
      getRandomAvatar()
        .then((avatarPath: string) => {
          this.animatableAvatar.slideInLeft(300);
          console.debug('RandomizeAvatarUtil: getRandomAvatar: avatarPath: ', avatarPath);
          this.setState({avatar_url: avatarPath});
        })
        .catch((error) => {
          console.debug('error getting random generated avatar.', error);
        });
    });
  };

  _onRefreshUsernamePressV2 = () => {
    if (this.props.isFTUE) {
      AWSManager.trackEvent('FTUE_REFRESH_USERNAME');
    } else {
      AWSManager.trackEvent('PROFILE_REFRESH_USERNAME');
    }
    trackTelemetryEvent({
      event: TelemetryEventName.SIGN_UP_CTA_CLICK,
      payload: {
        cta: 'player name shuffle',
      },
    });
    this.animatedUsernameView.flipOutX(500).then(() => {
      CurrentUserStore.fetchRandomGeneratedUsername()
        .then((newUsername: string) => {
          this.animatedUsernameView.flipInX(500);
          this.setState({
            username: newUsername,
            randomUsername: newUsername,
          });
        })
        .catch((error: any) => {
          console.debug('error getting random generated username.', error);
        });
    });
  };

  _showImagePicker = () => {
    trackTelemetryEvent({
      event: TelemetryEventName.SIGN_UP_CTA_CLICK,
      payload: {
        cta: 'player profile',
      },
    });

    // Call parent method logic
    if (Platform.OS === 'web') {
      this._showWebPicker();
    } else {
      this._showNativePicker();
    }
  };

  async _pushVersusFTUE() {
    // If we don't have the tournament or have already started joining a match, return
    if (!this.firstFTUETournament || this.matchTransactionId) {
      return;
    }

    this.matchTransactionId = await APIUtilities.createUUID();
    this._markFTUEStepComplete();
    const trainerProps = FTUEConversionsController.getTutorialTrainerProps('trainingMatch1Versus', {
      nameVisible: true,
    });

    FTUETournamentController.startFTUEMatch(this.firstFTUETournament, {
      onJoinMatch: ({isBotMatch}) => {
        FTUEController.setHasJoinedFirstFTUEMatch(true);

        if (!FTUEConversionsController.enabled) return;

        if (isBotMatch) {
          FTUEConversionsController.advance('playTrainingMatch1');
        } else {
          // If we weren't able to match against a bot, we'll skip the
          // conversions flow since it depends on having the trainer bot
          FTUEConversionsController.skip();
        }
      },
      showLoading: Platform.OS !== 'web',
      // Only show the versus screen if we're doing the trainer bot match
      shouldShowVersus: ({isBotMatch}) => isBotMatch && !!trainerProps,
      getVersusProps: () => {
        return trainerProps
          ? {
              ftueTrainerProps: trainerProps,
              timerHidden: true,
            }
          : {};
      },
      transactionID: this.matchTransactionId,
    });
  }

  _markFTUEStepComplete() {
    FTUEController.setHasCreatedProfile(true);
    FTUEController.setHasCompletedWelcomeFTUE(true);
    SyncFTUEController.setHasSeenFirstSyncFTUEVersus(true);
  }

  _shouldShowBackButton(): boolean {
    const condition = this.props.shouldAllowExit || this.props.shouldAllowPop;
    // when split account creation enabled, don't show back button for avatar selection screen
    return condition;
  }

  _skipFTUEMatchesAndRenderHomeScreen() {
    this._markFTUEStepComplete();

    // make sure we're also disabling the old FTUE
    FTUEController.skipFTUE();

    // skip match select tutorials
    FTUEController.setHasSeenMatchSelectTutorial(true);
    FTUEController.setHasSeenMatchStreamTutorial(true);
    FTUEController.setHasSeenVersusTutorials();

    // skip PLOW tutorials
    FTUEController.setHasSeenPLOWCompletedTutorialTwo(true);
    FTUEController.setHasSeenPLOWCompletedTutorialOne(true);

    // skip sync match select tutorial
    SyncFTUEController.setSyncFTUEComplete(true);
    SyncFTUEController.setHasSeenSyncMatchSelectTutorial(true);

    // trigger events so HomeScreen can update accordingly
    GlobalEventEmitter.emit('SyncFTUESkipped');
    GlobalEventEmitter.emit('toggleBarShadows', false);

    NavigationManager.popToRoot();
  }

  // CENTER

  _renderCenterContentHeading() {
    return <View />;
  }

  // HEADER

  _renderHeaderBackButton(): React.ReactNode | null {
    if (this._shouldShowBackButton()) {
      return (
        <View style={styles.buttonView}>
          <TouchableOpacity
            style={styles.backButtonTouchable}
            onPress={this._handleBack.bind(this)}
            hitSlop={{top: 6, left: 14, bottom: 6, right: 60}}
            accessible={false}
          >
            <View
              style={styles.backButtonContent}
              accessible={true}
              accessibilityLabel={'back_button'}
              accessibilityRole={'button'}
            >
              <SkillzImage
                source={'ic_chevron_left_skz.png'}
                style={{width: 50, height: 50}}
                resizeMode={'stretch'}
                tintColor={textColorPrimary.color}
              />
            </View>
          </TouchableOpacity>
        </View>
      );
    } else {
      return null;
    }
  }

  _renderHeader(): React.ReactNode {
    return (
      <View style={{justifyContent: 'center', alignItems: 'center'}}>
        <SkillzSVG
          style={styles.skillzPoweredS}
          resizeMode={'contain'}
          source={'ic_skillz_s.svg'}
        />
        <SkillzImage
          style={styles.skillzPoweredV2}
          resizeMode={'contain'}
          source={'ic_skillz_powered.png'}
          tintColor={'#3A3A3A'}
        />
      </View>
    );
  }

  // FOOTER

  _renderFooterButton(options: FooterButtonOptions): React.ReactNode {
    if (Platform.OS === 'web') return null;

    return (
      <TouchableOpacity
        onPress={options.onPress}
        accessible={true}
        accessibilityLabel={options.accessibilityLabel}
        disabled={this.state.isButtonDisabled}
        testID={options.testID}
      >
        <View>
          <Label style={styles.liveEventCodeUnderline}>{options.buttonLabel}</Label>
          <Label style={styles.liveEventCodeSubtext}>{_m('Click Here')}</Label>
        </View>
      </TouchableOpacity>
    );
  }

  _renderFooter(): React.ReactNode {
    const findEventButtonOptions: FooterButtonOptions = {
      buttonLabel: I18n.message('At A Live Event, Or Event Code In Hand?'),
      onPress: this._handleFindEvent.bind(this),
      accessibilityLabel: 'find_event',
      testID: TEST_IDS.CREATE_PROFILE_SCREEN_FOOTER_FIND_EVENT_BUTTON,
    };

    return (
      <View style={styles.footerBarContainerV2} onLayout={this._notifySDKHasLoaded}>
        <View />
        <Label style={styles.existingAccountLabelV2}>{_m('Already Have An Account?')}</Label>
        <SkillzButton
          containerStyle={styles.signInButtonV2}
          type={'Primary'}
          onPress={this._handleSignIn}
          testID={TEST_IDS.SIGN_IN}
          branding='Skillz'
          size='L'
          text={_m('Sign In')}
        />
        {this._renderFooterButton(findEventButtonOptions)}
        <View />
      </View>
    );
  }

  _setTOS(show: boolean) {
    trackTelemetryEvent({
      event: TelemetryEventName.SIGN_UP_CTA_CLICK,
      payload: {
        cta: 'terms of service',
      },
    });
    this.setState({showTermsOfUse: show});
  }

  _renderTermsOfServiceCTA() {
    return (
      <View style={{marginTop: 10}}>
        <Trans
          parent={Text}
          i18nKey={'TermsOfServiceCTAV2'}
          t={_m}
          components={{
            body: <Text style={styles.termsAndServiceTextV2} />,
            tos: (
              <Text
                style={[styles.termsAndServiceTextV2, styles.termsAndServiceSubText]}
                onPress={() => {
                  if (Platform.OS === 'web') {
                    window.open(TERM_OF_SERVICE_URL, '_blank');
                  } else {
                    LoadingIndicator.show();
                    this._setTOS(true);
                  }
                }}
              />
            ),
          }}
          children={<Text />} // needed to pass jest test due to issues with Trans component
        />
      </View>
    );
  }

  _renderTermsofUse(): React.ReactElement {
    return (
      <SafeAreaView style={styles.termsOfUseContainer}>
        <WebView
          source={{uri: TERMS_OF_SERVICE_URL}}
          onLoad={(): void => LoadingIndicator.hide()}
        />
        <TouchableOpacity
          style={{
            position: 'absolute',
            right: 0,
            top: -10,
            width: 40,
            height: 40,
          }}
          onPress={() => {
            this._setTOS(false);
          }}
        >
          <SkillzImage source={'close_button_icon.svg'} style={{height: 20, width: 20}} />
        </TouchableOpacity>
      </SafeAreaView>
    );
  }

  _renderMatchCodeFTUE(): React.ReactNode {
    return (
      <TouchableOpacity
        onPress={this._handleFindEvent}
        style={[styles.centerContentContainer, {height: 50, justifyContent: 'center'}]}
      >
        <View style={{flexDirection: 'row'}}>
          <View>
            <Label style={styles.matchCodeTextContainer}>
              {I18n.message('Have a match code?')}
            </Label>
          </View>
          <View testID={TEST_IDS.TAP}>
            <Label style={[styles.matchCodeTextContainer, {color: 'blue'}]}>
              {I18n.message(' Tap Here')}
            </Label>
          </View>
        </View>
      </TouchableOpacity>
    );
  }

  _renderPickInfoTitle(title: string): React.ReactElement {
    return (
      <View
        accessible={true}
        accessibilityLabel={title}
        testID={TEST_IDS.EDIT_PROFILE_SCREEN_PICK_INFO_TITLE}
      >
        <Label style={styles.pickInfoTitleV2}>{title}</Label>
      </View>
    );
  }

  _renderChangeMessage(changeMessage: string): React.ReactElement {
    return (
      <View
        accessible={true}
        accessibilityLabel={changeMessage}
        testID={TEST_IDS.EDIT_PROFILE_SCREEN_CHANGE_MESSAGE}
      >
        <Label style={styles.changeMessageV2}>{changeMessage}</Label>
      </View>
    );
  }

  _renderPickAvatar(): React.ReactElement {
    console.debug('RandomizeAvatarUtil: getRandomAvatar: avatarPath: ', this.state.avatar_url);
    return (
      <View
        style={styles.avatarContainerV2}
        accessible={true}
        testID={TEST_IDS.EDIT_PROFILE_SCREEN_PICK_AVATAR}
        accessibilityLabel={'pick_avatar'}
      >
        <View style={[StyleSheet.absoluteFill, {overflow: 'hidden', borderRadius: 100, margin: 4}]}>
          <Animatable.View
            style={styles.avatarImageV2}
            ref={(ref) => {
              this.animatableAvatar = ref;
            }}
          >
            <SkillzImage
              style={styles.avatarImageV2}
              source={this.state.avatar_url ?? 'avatar_pending_generic_skz.png'}
            />
          </Animatable.View>
        </View>
        {this._renderChangeAvatarButtons()}
      </View>
    );
  }

  _renderChangeAvatarButtons(): React.ReactElement {
    const shouldAllowRefresh = ClientPropertyConstants.getAvatarRandomizerEnabled();
    const shouldAllowFullEdit =
      !ClientPropertyConstants.getAvatarRandomizerOnlyFTUE() || !this.props.isFTUE;

    const refreshAvatar = (
      <View style={styles.refreshAvatarIconV2}>
        <TouchableOpacity
          onPress={this._onRefreshAvatarPressV2}
          testID={'refresh_avatar_button'}
          hitSlop={{top: 20, left: 20, right: 20, bottom: 20}}
        >
          <SkillzSVG style={styles.refreshButtonIconV2} source={'ic_random_button.svg'} />
        </TouchableOpacity>
      </View>
    );

    const editAvatar = (
      <View style={styles.editAvatarIconV2}>
        <TouchableOpacity
          onPress={this._showImagePicker}
          testID={'edit_avatar_button'}
          hitSlop={{top: 20, left: 20, right: 20, bottom: 20}}
        >
          <SkillzSVG style={styles.editButtonIconV2} source={'ic_camera.svg'} />
        </TouchableOpacity>
      </View>
    );

    return (
      <View style={styles.editAvatarButtonsContainer}>
        {shouldAllowFullEdit || !shouldAllowRefresh ? editAvatar : <React.Fragment />}
        {shouldAllowRefresh ? refreshAvatar : <React.Fragment />}
      </View>
    );
  }

  _renderRefreshUsernameButton(renderBoth: boolean): React.ReactElement {
    if (
      (this.state.canChangeUsername &&
        ClientPropertyConstants.getFTUESplitAccountCreationEnabled()) ||
      ClientPropertyConstants.getUsernameRandomizerEnabled()
    ) {
      return (
        <View style={styles.refreshButtonContainerV2}>
          <TouchableOpacity
            onPress={this._onRefreshUsernamePressV2}
            testID={'refresh_username_button'}
            hitSlop={{top: 20, left: 20, right: 20, bottom: 20}}
          >
            <SkillzSVG style={styles.refreshButtonIconV2} source={'ic_random_button.svg'} />
          </TouchableOpacity>
        </View>
      );
    } else {
      return <React.Fragment />;
    }
  }

  _renderPickUsername(
    _style: {changeUsername: ViewStyle; changeUsernameContainer: ViewStyle},
    renderBoth: boolean
  ): React.ReactElement {
    const opacity = this.state.canChangeUsername ? {opacity: 1} : {opacity: 0.3};
    // when randomizer enabled, left align username text
    // when refresh button is there, make sure there's no text overlap
    const refreshPadding = ClientPropertyConstants.getUsernameRandomizerEnabled()
      ? {paddingRight: 35}
      : {};
    return (
      <View style={styles.changeUsernameContainerV2}>
        <Animatable.View
          style={styles.changeUsernameWrapper}
          ref={(ref) => {
            this.animatedUsernameView = ref;
          }}
        >
          <TextInput
            style={[styles.changeUsernameV2, opacity, refreshPadding]}
            onFocus={this._clearTextIfRandom}
            onChangeText={this._handleUsernameChange}
            value={this.state.username}
            editable={this.state.canChangeUsername}
            accessible={true}
            accessibilityLabel={'Username text field input'}
            testID={TEST_IDS.EDIT_PROFILE_SCREEN_USERNAME_INPUT}
          />
          {this._renderRefreshUsernameButton(renderBoth)}
        </Animatable.View>
      </View>
    );
  }

  render(): React.ReactElement {
    const pickInfoTitle = _m('Select Your Avatar');
    const customizeSubtitle = ClientPropertyConstants.getAvatarRandomizerOnlyFTUE()
      ? _m('You can customize after your 5th match')
      : CHANGE_MESSAGE;

    // render both avatar and username selection if client prop disabled or usernameSelection not given
    const renderBoth = true;
    return (
      <View
        style={[
          styles.mainContainerV2,
          Platform.OS === 'ios'
            ? {minHeight: '100%'}
            : {minHeight: Dimensions.get('window').height},
        ]}
      >
        <Optional test={this.state.showTermsOfUse}>{this._renderTermsofUse()}</Optional>
        <Optional test={!this.state.showTermsOfUse}>
          <View style={styles.mainContainerV2}>
            {this._renderHeader()}
            <View style={{width: '100%', alignItems: 'center'}}>
              {this._renderPickInfoTitle(pickInfoTitle)}
              {this._renderChangeMessage(customizeSubtitle)}
              {this._renderPickAvatar()}
              {this._renderPickUsername(styles, renderBoth)}
              <SkillzButton
                type={'Secondary'}
                containerStyle={styles.nextButtonV2}
                onPress={this._onPressDone}
                text={NEXT_BUTTON_TITLE}
                size={'L'}
                branding='Skillz'
                testID={TEST_IDS.EDIT_PROFILE_SCREEN_SUBMIT_BUTTON}
              />
              {this._renderTermsOfServiceCTA()}
            </View>
            {this._renderFooter()}
          </View>
        </Optional>
      </View>
    );
  }
}
